import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DriveFileMoveOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import FieldLabel from "components/common/FieldLabel";
import UserAvatar from "components/common/UserAvatar";
import FormFooter, {
  FOOTER_BUTTON_SX,
} from "components/userManagement/form/FormFooter";
import FormHeader from "components/userManagement/form/FormHeader";
import { pushNotification } from "features/notificationSlice";
import {
  type SecureFile,
  type SecurePerson,
  useCreateSecureCollectionMutation,
  useGetSecureMoveCheckQuery,
  useGetSecureProjectCollectionsQuery,
  useGetSecureProjectsQuery,
  useMoveSecureFileMutation,
} from "features/secureUploads/secureUploads";
import { useViewAs } from "hooks/useViewAs";
import { formatApiError } from "utils/formatApiError";

import { COLLECTION_NAME_MAX } from "./CollectionDialogs";

/** "collection": within the file's project; "project": into another project. */
export type MoveMode = "collection" | "project";

const ROOT = "root";
const NEW = "new";

function People({ people }: { people: SecurePerson[] }) {
  return (
    <Box sx={{ display: "grid", gap: 0.75, mt: 1 }}>
      {people.map((person) => (
        <Box
          key={person.id}
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <UserAvatar person={person} size={22} />
          <Typography variant="body2">{person.name}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** Move a file between a project's collections, or into another project after
 * seeing who would lose it and who would newly see it. */
export default function MoveFileDialog({
  file,
  mode,
  onClose,
}: {
  file: SecureFile | null;
  mode: MoveMode;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const { readOnly } = useViewAs();
  const [projectChoice, setProjectChoice] = useState("");
  const [collectionChoice, setCollectionChoice] = useState(ROOT);
  const [newName, setNewName] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const open = file !== null;

  useEffect(() => {
    setProjectChoice("");
    // Inside a project the dialog starts where the file is: Move waits for a change.
    setCollectionChoice(
      mode === "collection" && file?.collection_id != null
        ? String(file.collection_id)
        : ROOT,
    );
    setNewName("");
    setFailure(null);
  }, [file, mode]);

  const sameProject = mode === "collection";
  const projectId = sameProject
    ? (file?.project_id ?? null)
    : Number(projectChoice) || null;
  const { data: openProjects } = useGetSecureProjectsQuery(
    { client_id: file?.client_id ?? 0, status: "open", page_size: 200 },
    { skip: !open || sameProject || !file?.client_id },
  );
  const { data: collections = [] } = useGetSecureProjectCollectionsQuery(
    projectId ?? 0,
    { skip: !open || !projectId },
  );
  const collectionId =
    collectionChoice === ROOT || collectionChoice === NEW
      ? null
      : Number(collectionChoice);
  const { data: check, isFetching: checking } = useGetSecureMoveCheckQuery(
    {
      id: file?.id ?? 0,
      project_id: projectId ?? 0,
      collection_id: collectionId,
    },
    { skip: !open || sameProject || !projectId },
  );
  const [move, { isLoading: moving }] = useMoveSecureFileMutation();
  const [createCollection, { isLoading: creating }] =
    useCreateSecureCollectionMutation();
  const busy = moving || creating;

  if (!file) return null;

  const targets = (openProjects?.results ?? []).filter(
    (project) => project.id !== file.project_id,
  );
  // The file's own collection is offered while the list is still loading.
  const choices =
    sameProject &&
    file.collection_id != null &&
    !collections.some((each) => each.id === file.collection_id)
      ? [
          { id: file.collection_id, name: file.collection_name ?? "" },
          ...collections,
        ]
      : collections;
  const losing = check?.losing ?? [];
  const gaining = check?.gaining ?? [];
  const here =
    sameProject &&
    collectionChoice !== NEW &&
    (collectionId ?? null) === (file.collection_id ?? null);
  const ready =
    Boolean(projectId) &&
    !here &&
    !(collectionChoice === NEW && !newName.trim()) &&
    (sameProject || (!checking && check !== undefined));

  const submit = async () => {
    if (!projectId) return;
    setFailure(null);
    try {
      let target = collectionId;
      if (collectionChoice === NEW) {
        const made = await createCollection({
          projectId,
          name: newName.trim(),
        }).unwrap();
        target = made.id;
      }
      await move({
        id: file.id,
        project_id: projectId,
        collection_id: target,
        add_members: losing.length > 0,
      }).unwrap();
      dispatch(
        pushNotification({ message: "File moved.", severity: "success" }),
      );
      onClose();
    } catch (error) {
      setFailure(
        formatApiError(
          (error as { data?: unknown })?.data,
          "The file could not be moved.",
        ),
      );
    }
  };

  const projectName =
    targets.find((project) => project.id === projectId)?.name ?? "the project";
  const confirmLabel =
    losing.length > 0
      ? `Add ${losing.length === 1 ? losing[0].name : `${losing.length} people`} to the project and move`
      : "Move";

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <FormHeader
          avatar={<DriveFileMoveOutlined fontSize="small" />}
          title={
            sameProject
              ? file.collection_id == null
                ? "Add to Collection"
                : "Move File"
              : file.project_id == null
                ? "Add to Project"
                : "Move to Project"
          }
          onClose={onClose}
          closeDisabled={busy}
        />
        <Box sx={{ p: 3, display: "grid", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <b>{file.file_name}</b>
            {check ? ` - from ${check.from}` : ""}
          </Typography>
          {sameProject ? null : (
            <Box>
              <FieldLabel required>Project</FieldLabel>
              <TextField
                select
                fullWidth
                size="small"
                value={projectChoice}
                disabled={busy}
                SelectProps={{ displayEmpty: true }}
                onChange={(event) => {
                  setProjectChoice(event.target.value);
                  setCollectionChoice(ROOT);
                }}
              >
                <MenuItem value="" disabled>
                  Choose an open project
                </MenuItem>
                {targets.map((project) => (
                  <MenuItem key={project.id} value={String(project.id)}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
          {projectId ? (
            <Box>
              <FieldLabel>Collection</FieldLabel>
              <TextField
                select
                fullWidth
                size="small"
                value={collectionChoice}
                disabled={busy}
                onChange={(event) => setCollectionChoice(event.target.value)}
              >
                <MenuItem value={ROOT}>No collection</MenuItem>
                {choices.map((collection) => (
                  <MenuItem key={collection.id} value={String(collection.id)}>
                    {collection.name}
                  </MenuItem>
                ))}
                <MenuItem value={NEW}>+ New collection</MenuItem>
              </TextField>
              {collectionChoice === NEW ? (
                <TextField
                  fullWidth
                  size="small"
                  autoFocus
                  sx={{ mt: 1.5 }}
                  placeholder="Name the new collection"
                  value={newName}
                  disabled={busy}
                  onChange={(event) => setNewName(event.target.value)}
                  inputProps={{ maxLength: COLLECTION_NAME_MAX }}
                />
              ) : null}
            </Box>
          ) : null}
          {losing.length > 0 ? (
            <Alert severity="warning">
              {losing.length === 1
                ? `${losing[0].name} isn't on ${projectName} and would lose access to this file.`
                : `These people aren't on ${projectName} and would lose access to this file.`}
              <People people={losing} />
            </Alert>
          ) : null}
          {gaining.length > 0 ? (
            <Alert severity="info">
              {gaining.length === 1
                ? "This person will newly see the file there:"
                : "These people will newly see the file there:"}
              <People people={gaining} />
            </Alert>
          ) : null}
          {failure ? <Alert severity="error">{failure}</Alert> : null}
        </Box>
      </DialogContent>
      <FormFooter
        mode="edit"
        isFirstStep
        onBack={() => undefined}
        onCancel={onClose}
      >
        <Button
          variant="contained"
          size="small"
          onClick={submit}
          disabled={!ready || busy || readOnly}
          sx={FOOTER_BUTTON_SX}
        >
          {busy ? "Moving..." : confirmLabel}
        </Button>
      </FormFooter>
    </Dialog>
  );
}
